import { useState } from "react";
import { Dialog } from "@mui/material";
import { toast } from "react-toastify";
import { createPartnerLink, errorText, partnerUrl } from "./affiliateApi";
import styles from "./affiliates.module.css";

const copy = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copy failed, select the text and copy it yourself");
  }
};

// The affiliate's read-only partner page: a secret link plus a password.
// The password is only shown right after it is made.
export default function PartnerLinkDialog({ affiliate, onClose, onCreated }) {
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmNew, setConfirmNew] = useState(false);
  const token = fresh?.token || affiliate.shareToken;

  const make = async () => {
    setBusy(true);
    try {
      const link = await createPartnerLink(affiliate._id);
      setFresh(link);
      setConfirmNew(false);
      onCreated(link.token);
    } catch (err) {
      toast.error(errorText(err, "Could not make a partner link"));
    } finally {
      setBusy(false);
    }
  };

  const message = fresh
    ? `Your U-Send partner overview: ${partnerUrl(fresh.token)}\nPassword: ${fresh.password}`
    : "";

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ className: styles.dialog }}>
      <div className={styles.form}>
        <div className={styles.dialogTitle}>Partner page for {affiliate.name}</div>
        <p className={styles.sub}>
          A read-only page with the stores linked to this affiliate and their shipped orders. It shows the owner's
          name, but no email or phone.
        </p>

        {token && (
          <>
            <label htmlFor="partner-link">Link</label>
            <div className={styles.copyRow}>
              <input id="partner-link" readOnly value={partnerUrl(token)} onFocus={(e) => e.target.select()} />
              <button type="button" className={styles.secondary} onClick={() => copy(partnerUrl(token), "Link")}>
                Copy
              </button>
            </div>
          </>
        )}

        {fresh && (
          <>
            <label htmlFor="partner-password">Password</label>
            <div className={styles.copyRow}>
              <input id="partner-password" readOnly value={fresh.password} onFocus={(e) => e.target.select()} />
              <button type="button" className={styles.secondary} onClick={() => copy(fresh.password, "Password")}>
                Copy
              </button>
            </div>
            <p className={styles.notice}>Save or send the password now. It won't be shown again.</p>
            <button type="button" className={styles.primary} onClick={() => copy(message, "Message")}>
              Copy link and password as one message
            </button>
          </>
        )}

        {!token && <p className={styles.hint}>This affiliate has no partner page yet.</p>}
        {token && !fresh && (
          <p className={styles.hint}>Lost the password? Make a new link. The old link and password then stop working.</p>
        )}

        {confirmNew ? (
          <div className={styles.confirm}>
            <span>Make a new link? The current link and password stop working right away.</span>
            <div className={styles.actions}>
              <button type="button" className={styles.secondary} onClick={() => setConfirmNew(false)}>
                Keep current
              </button>
              <button type="button" className={styles.danger} onClick={make} disabled={busy}>
                Make new link
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.actions}>
            <button type="button" className={styles.secondary} onClick={onClose}>
              Close
            </button>
            {!fresh && (
              <button
                type="button"
                className={styles.primary}
                onClick={() => (token ? setConfirmNew(true) : make())}
                disabled={busy}
              >
                {token ? "Make new link and password" : "Make partner link"}
              </button>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
